import React from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Button,
  Collapse,
  IconButton,
  Chip,
  Grid,
  Alert,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

type Item = {
  id: string | number;
  label: string;
  checked?: boolean;
  viewUrl?: string;
};

type Props = {
  title?: string;
  dueDate?: string | Date | null;
  timeLeft?: string;
  status?: string;
  items?: Item[];
  initialComment?: string;
  readOnly?: boolean;
  saving?: boolean;
  onSave?: (payload: {
    checked: Record<string, boolean>;
    comment: string;
  }) => Promise<void> | void;
  onClear?: () => void;
  onStatusChange?: (status: string) => Promise<void> | void;
};

const formatMMDDYYYY = (d?: string | Date | null) => {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (isNaN(date.getTime())) return '—';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const AcknowledgementTask: React.FC<Props> = ({
  title = 'Acknowledgement',
  dueDate = null,
  timeLeft = '00:00:00 Left',
  status = 'To Do',
  items = [],
  initialComment = '',
  readOnly = false,
  saving = false,
  onSave,
  onClear,
  onStatusChange
}) => {
  const [expanded, setExpanded] = React.useState(true);
  const [checkedMap, setCheckedMap] = React.useState<Record<string, boolean>>({});
  const [comment, setComment] = React.useState(initialComment);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [lastSaveTime, setLastSaveTime] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [statusUpdating, setStatusUpdating] = React.useState(false);
  
  const limit = 500;
  const isOverdue = timeLeft === 'Overdue';
  const isOnHold = status === 'On Hold';
  const canEdit = !readOnly && !isOnHold;
  const shouldShowTimer = !!timeLeft?.trim();
  const isOutsideHours = () => {
    const now = new Date();
    const hour = now.getHours() + now.getMinutes() / 60;
    const day = now.getDay();
    return hour < 9 || hour >= 18 || day === 0 || day === 6;
  };

  const timerHint = isOnHold
    ? '(SLA timer stopped as the task status has been changed to On Hold.)'
    : isOutsideHours()
    ? '(SLA timer paused - will resume during working hours (9:00 AM - 6:00 PM))'
    : '';

  const timeLeftStyle = {
    color: isOverdue ? '#d32f2f' : 'inherit',
    fontWeight: isOverdue ? 'bold' : 'normal',
    fontSize: '12px'
  };

  // Initialize state only once when items first load
  React.useEffect(() => {
    if (items.length > 0 && !isInitialized) {
      const newCheckedMap = items.reduce((acc, item) => {
        acc[String(item.id)] = !!item.checked;
        return acc;
      }, {} as Record<string, boolean>);
      
      setCheckedMap(newCheckedMap);
      setComment(initialComment);
      setIsInitialized(true);
    }
  }, [items, initialComment, isInitialized]);

  // Update comment when initialComment changes
  React.useEffect(() => {
    if (isInitialized) {
      setComment(initialComment);
    }
  }, [initialComment, isInitialized]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setStatusUpdating(true);
      await onStatusChange?.(newStatus);
      handleMenuClose();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleToggle = (id: string | number) => {
    if (!canEdit) return;
    
    const stringId = String(id);
    const newCheckedMap = {
      ...checkedMap,
      [stringId]: !checkedMap[stringId]
    };
    
    setCheckedMap(newCheckedMap);
    
    // Auto-change status to IN_PROGRESS when user starts making changes
    if (status === 'To Do' && Object.values(newCheckedMap).some(checked => checked)) {
      handleStatusChange('IN_PROGRESS');
    }
  };

  const handleCommentChange = (value: string) => {
    if (!canEdit) return;
    
    if (value.length <= limit) {
      setComment(value);
      
      // Auto-change status to IN_PROGRESS when user starts typing
      if (status === 'To Do' && value.trim().length > 0) {
        handleStatusChange('IN_PROGRESS');
      }
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;

    // Validation: Check if at least one document is acknowledged OR comment is provided
    const hasAnyChecked = Object.values(checkedMap).some((checked) => checked);
    const hasComment = comment.trim().length > 0;
    
    if (!hasAnyChecked && !hasComment) {
      alert('Please acknowledge at least one document or provide a comment before saving.');
      return;
    }

    try {
      await onSave?.({ checked: checkedMap, comment });
      setLastSaveTime(Date.now()); // Mark when we saved
    } catch (error) {
      console.error('Error saving acknowledgement:', error);
      alert('Failed to save acknowledgement. Please try again.');
    }
  };

  const handleClear = () => {
    if (!canEdit) return;
    
    // Clear all checkboxes locally only
    const clearedMap = Object.keys(checkedMap).reduce(
      (acc, k) => ({ ...acc, [k]: false }),
      {} as Record<string, boolean>
    );
    
    setCheckedMap(clearedMap);
    setComment('');
    onClear?.(); // This will call the parent's clear function (if provided)
  };

  const handleViewDocument = (url: string) => {
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const statusColor = {
    'To Do': 'info',
    'In Progress': 'primary',
    'On Hold': 'warning',
    Completed: 'success'
  }[status] as any;

  const allChecked = items.length > 0 && Object.values(checkedMap).every((checked) => checked);
  const hasComment = comment.trim().length > 0;
  const hasAnyChecked = Object.values(checkedMap).some((checked) => checked);
  const canSave = hasAnyChecked || hasComment;

  return (
    <Box width="100%" sx={{ bgcolor: 'transparent' }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: isOnHold ? '#fff3cd' : '#eef0ff',
          borderRadius: 2,
          px: 2,
          py: 1,
          mb: 2,
          border: isOnHold ? '1px solid #ffc107' : 'none'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 1,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <DescriptionOutlinedIcon color="primary" />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize={16}>
              {title}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography fontSize={12} color="text.secondary">
                Due Date: {formatMMDDYYYY(dueDate)}
              </Typography>
              {shouldShowTimer && (
                <Typography style={timeLeftStyle}>
                  {isOverdue ? timeLeft : `${timeLeft} Left`}
                  {timerHint ? ` ${timerHint}` : ''}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={status}
            color={statusColor}
            sx={{ borderRadius: 2, px: 1.5 }}
          />
          
          {/* Status Management Menu */}
          {!readOnly && status !== 'Completed' && onStatusChange && (
            <>
              <IconButton 
                size="small" 
                onClick={handleMenuOpen}
                disabled={statusUpdating || saving}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {status !== 'On Hold' && (
                  <MenuItem onClick={() => handleStatusChange('ON_HOLD')}>
                    <ListItemIcon>
                      <PauseCircleOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Put On Hold</ListItemText>
                  </MenuItem>
                )}
                {status === 'On Hold' && (
                  <MenuItem onClick={() => handleStatusChange('IN_PROGRESS')}>
                    <ListItemIcon>
                      <PlayCircleOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Resume Progress</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          <IconButton size="small" onClick={() => setExpanded((s) => !s)}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 2 }}>
          {status === 'Completed' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              All documents have been acknowledged. Acknowledgments cannot be
              modified once completed.
            </Alert>
          )}

          {isOnHold && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This task is currently on hold. Resume progress to continue working on acknowledgments.
            </Alert>
          )}

          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Onboarding Documents
          </Typography>

          <Grid container spacing={2}>
            {items.map((item) => {
              const isChecked = !!checkedMap[String(item.id)];
              
              return (
                <Grid item xs={12} key={item.id}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: isChecked ? 'primary.main' : 'grey.300',
                      borderRadius: 1,
                      bgcolor: isChecked ? '#f0f7ff' : isOnHold ? '#f8f9fa' : 'white',
                      transition: 'all 0.2s ease',
                      opacity: isOnHold ? 0.7 : 1,
                      '&:hover': {
                        borderColor: canEdit ? 'primary.main' : 'grey.300',
                        cursor: canEdit ? 'pointer' : 'default',
                        bgcolor: canEdit ? '#f8f9fa' : (isOnHold ? '#f8f9fa' : 'white')
                      }
                    }}
                    onClick={() => canEdit && handleToggle(item.id)}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggle(item.id);
                        }}
                        disabled={!canEdit}
                        sx={{
                          color: '#4e56ff',
                          '&.Mui-checked': { color: '#4e56ff' },
                          '&.Mui-disabled': { opacity: 0.6 }
                        }}
                      />
                      <Typography
                        fontWeight={600}
                        sx={{
                          color: isChecked ? 'primary.main' : 'text.primary'
                        }}
                      >
                        {item.label}
                      </Typography>
                    </Box>

                    {item.viewUrl && item.viewUrl !== '#' ? (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDocument(item.viewUrl!);
                        }}
                        sx={{ minWidth: 80 }}
                      >
                        View
                      </Button>
                    ) : (
                      <Box sx={{ width: 80 }} />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <Box mt={3}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>
              Comment {!readOnly && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <TextField
              placeholder="Please provide your acknowledgment comment here..."
              fullWidth
              multiline
              minRows={3}
              value={comment}
              onChange={(e) => handleCommentChange(e.target.value)}
              disabled={!canEdit}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  bgcolor: !canEdit ? 'grey.100' : 'white'
                }
              }}
            />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={1}
            >
              <Typography fontSize={12} color="text.secondary">
                {`${comment.length}/${limit}`}
              </Typography>

              {canEdit && (
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClear}
                    disabled={saving || statusUpdating}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={saving || statusUpdating || !canSave}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              )}
            </Box>

            {canEdit && allChecked && hasComment && (
              <Alert severity="success" sx={{ mt: 2 }}>
                All documents acknowledged and comment provided. The acknowledgment task will be completed when you save.
              </Alert>
            )}

            {canEdit && (hasAnyChecked || hasComment) && !(allChecked && hasComment) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Progress saved. {!allChecked && 'Acknowledge all documents'}{!allChecked && !hasComment && ' and '}{!hasComment && 'provide a comment'} to complete the task.
              </Alert>
            )}

            {statusUpdating && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Updating task status...
              </Alert>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default AcknowledgementTask;